import { ReactNode } from "react";
import { faArrowLeft, faArrowRight } from "@fortawesome/pro-regular-svg-icons";

import Icon from "common/components/Icon";
import Image, { ImageProps } from "common/components/Image";

import Form, { FormProps } from "client/common/components/forms/Form";
import Button, { ButtonProps } from "client/common/components/buttons/Button";
import LoadingContainer from "client/common/components/loading/LoadingContainer";
import FetchErrorAlert, {
  FetchErrorAlertProps,
} from "client/common/components/errors/FetchErrorAlert";
import Tooltip from "client/common/components/content/Tooltip";

import styles from "../styles/CheckoutStepLayout.module.less";

type CheckoutStepLayoutProps = {
  children: ReactNode;
};

const CheckoutStepLayout = ({ children }: CheckoutStepLayoutProps) => {
  return <div className={styles.layout}>{children}</div>;
};

type CheckoutStepSectionProps = { children: ReactNode };

const CheckoutStepSection = ({ children }: CheckoutStepSectionProps) => {
  return <div className={styles.section}>{children}</div>;
};

type CheckoutStepItemProps = { children: ReactNode };

const CheckoutStepItem = ({ children }: CheckoutStepItemProps) => {
  return <div className={styles.item}>{children}</div>;
};

const CheckoutStepItemInfo = ({ children }: CheckoutStepItemProps) => {
  return <div className={styles.info}>{children}</div>;
};

const CheckoutStepItemContent = ({ children }: CheckoutStepItemProps) => {
  return <div className={styles.content}>{children}</div>;
};

type CheckoutStepHeaderProps = { children: ReactNode };

const CheckoutStepHeader = ({ children }: CheckoutStepHeaderProps) => {
  return <div className={styles.header}>{children}</div>;
};

const CheckoutStepHeading = ({ children }: CheckoutStepHeaderProps) => {
  return <h4 className={styles.heading}>{children}</h4>;
};

const CheckoutStepSubheading = ({ children }: CheckoutStepHeaderProps) => {
  return <div className={styles.subheading}>{children}</div>;
};

const CheckoutStepSubheadingEmphasis = ({ children }: CheckoutStepHeaderProps) => {
  return <span className={styles.emphasis}>{children}</span>;
};

const CheckoutStepTitle = ({ children }: CheckoutStepHeaderProps) => {
  return <h3 className={styles.title}>{children}</h3>;
};

type CheckoutStepFormProps = FormProps & { className?: never };

const CheckoutStepForm = ({ children, ...rest }: CheckoutStepFormProps) => {
  return (
    <Form {...rest} className={styles.form}>
      {children}
    </Form>
  );
};

type CheckoutStepDescriptionProps = {
  children: ReactNode;
};

const CheckoutStepDescription = ({ children }: CheckoutStepDescriptionProps) => {
  return <div className={styles.description}>{children}</div>;
};

const CheckoutStepLoading = () => {
  return (
    <CheckoutStepLayout>
      <LoadingContainer />
    </CheckoutStepLayout>
  );
};

type CheckoutStepCoverImageProps = Omit<ImageProps, "className">;

const CheckoutStepCoverImage = (props: CheckoutStepCoverImageProps) => {
  return <Image {...props} className={styles.coverImage} />;
};

type CheckoutStepActionsProps = {
  children: ReactNode;
};

const CheckoutStepActions = ({ children }: CheckoutStepActionsProps) => {
  return <div className={styles.actions}>{children}</div>;
};

type CheckoutStepActionButtonProps = {
  children: React.ReactNode;
  onClick: () => void;
  className?: string;
  disabled?: boolean;
  disabledHelp?: string;
  isLoading?: boolean;
  variant?: ButtonProps["variant"];
};

const CheckoutStepActionButton = ({
  children,
  className,
  disabled,
  disabledHelp = "",
  ...props
}: CheckoutStepActionButtonProps) => {
  return (
    <Tooltip className={styles.tooltip} content={disabledHelp} isEnabled={!!disabled}>
      <Button
        {...props}
        disabled={disabled}
        className={`${styles.action} ${styles.button} ${className}`}
      >
        <span className={styles.inner}>{children}</span>
      </Button>
    </Tooltip>
  );
};

const CheckoutStepActionButtonNext = ({ children, ...props }: CheckoutStepActionButtonProps) => {
  return (
    <CheckoutStepActionButton {...props} variant="primary" className={styles.next}>
      {children} <Icon icon={faArrowRight} className={styles.icon} />
    </CheckoutStepActionButton>
  );
};

const CheckoutStepActionButtonPrevious = ({
  children,
  ...props
}: CheckoutStepActionButtonProps) => {
  return (
    <div className={styles.action}>
      <button type="button" className={`${styles.button} ${styles.previous}`} {...props}>
        <span className={styles.inner}>
          <Icon icon={faArrowLeft} className={styles.icon} /> {children}
        </span>
      </button>
    </div>
  );
};

const CheckoutStepActionButtonSubmit = ({ children, ...props }: CheckoutStepActionButtonProps) => {
  return (
    <CheckoutStepActionButton {...props} variant="primary" className={styles.submit}>
      {children}
    </CheckoutStepActionButton>
  );
};

const CheckoutFetchErrorAlert = <T extends any>(props: FetchErrorAlertProps<T>) => {
  return (
    <div className={styles.error}>
      <FetchErrorAlert {...props} />
    </div>
  );
};

export default Object.assign(CheckoutStepLayout, {
  Section: CheckoutStepSection,
  Item: Object.assign(CheckoutStepItem, {
    Info: CheckoutStepItemInfo,
    Content: CheckoutStepItemContent,
  }),
  Header: CheckoutStepHeader,
  Heading: CheckoutStepHeading,
  Subheading: Object.assign(CheckoutStepSubheading, { Emphasis: CheckoutStepSubheadingEmphasis }),
  Form: CheckoutStepForm,
  Title: CheckoutStepTitle,
  Description: CheckoutStepDescription,
  Loading: CheckoutStepLoading,
  CoverImage: CheckoutStepCoverImage,
  Actions: CheckoutStepActions,
  ActionButton: Object.assign(CheckoutStepActionButton, {
    Next: CheckoutStepActionButtonNext,
    Previous: CheckoutStepActionButtonPrevious,
    Submit: CheckoutStepActionButtonSubmit,
  }),
  FetchErrorAlert: CheckoutFetchErrorAlert,
});
